export const FORTUNE_IMAGE_API = '/api/fortune-images/';

export interface FortuneImageDownload {
  readonly imageUrl: string;
  readonly downloadUrl: string;
  readonly expiresAt: number;
}

/** Only the rendered PNG is sent, never a birth date or the input form. */
export async function prepareFortuneImageDownload(file: File, signal?: AbortSignal): Promise<FortuneImageDownload> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort, { once: true });
  if (signal?.aborted) abort();
  const timeout = setTimeout(abort, 15_000);
  try {
    const response = await fetch(FORTUNE_IMAGE_API, {
      method: 'POST',
      headers: { 'Content-Type': 'image/png' },
      body: file,
      credentials: 'omit',
      cache: 'no-store',
      referrerPolicy: 'no-referrer',
      signal: controller.signal,
    });
    if (!response.ok) throw new Error('Image download preparation failed');
    const result: unknown = await response.json();
    if (!result || typeof result !== 'object' || !('path' in result) || !('expiresAt' in result)
      || typeof result.path !== 'string' || !/^\/api\/fortune-images\/[a-f0-9]{32}\.png$/.test(result.path)
      || typeof result.expiresAt !== 'number' || !Number.isFinite(result.expiresAt)
      || result.expiresAt <= Date.now() + 30_000 || result.expiresAt > Date.now() + 11 * 60_000) {
      throw new Error('Invalid image download response');
    }
    return { imageUrl: result.path, downloadUrl: `${result.path}?download=1`, expiresAt: result.expiresAt };
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener('abort', abort);
  }
}
